import type { GroupSubscriptionData } from '../groupSubscriptionTypes';

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;

/**
 * Pending invites count as assigned: they reserve the seat. Without that,
 * 99 filled + 5 pending → admin invites #100 → an invitee accepts → locked out.
 * Terminating seats still hold access until ends_at.
 */
export function countAssignedSeats(group: GroupSubscriptionData): number {
  const terminating = group.seats.filter((s) => s.status === 'terminating').length;
  return group.seats_filled + terminating + group.invites_pending;
}

// Clamped non-negative — the API can temporarily over-count during state transitions.
export function seatsRemaining(group: GroupSubscriptionData): number {
  return Math.max(0, group.max_seats - countAssignedSeats(group));
}

/**
 * Throwaway proration math — the real number must come from Stripe via
 * `Invoice.upcoming`. When BEE-18935 lands a `seat_change_preview` endpoint,
 * swap this helper for the API-backed estimate so the displayed number equals
 * the actual charge by construction.
 *
 * Period start isn't in the index payload, so we approximate as `renews_at - 1 year`.
 * Good enough for an estimate; not good enough for the actual charge.
 */
export function computeProratedCents(group: GroupSubscriptionData, extraSeats: number): number {
  if (extraSeats <= 0) return 0;
  if (!group.renews_at) return 0;
  const end = new Date(group.renews_at).getTime();
  const start = end - YEAR_MS;
  const now = Date.now();
  const totalMs = end - start;
  const remainingMs = Math.max(0, Math.min(end - now, totalMs));
  if (totalMs <= 0) return 0;
  const fraction = remainingMs / totalMs;
  return Math.round(group.per_seat_amount_cents * extraSeats * fraction);
}
